import {useMemo, useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link, useSearchParams} from 'react-router-dom'
import {useFormik} from 'formik'
import axios from 'axios'
import {apiUrl} from '../../../../api-utils/constantVariables'

const passwordRules = Yup.string()
  .min(12, 'At least 12 characters')
  .matches(/[A-Z]/, 'Need uppercase letter')
  .matches(/[a-z]/, 'Need lowercase letter')
  .matches(/[0-9]/, 'Need a number')
  .matches(/[^A-Za-z0-9]/, 'Need a special character')
  .required('Required')

const schema = Yup.object().shape({
  new_password: passwordRules,
  confirm: Yup.string()
    .oneOf([Yup.ref('new_password')], 'Passwords must match')
    .required('Required'),
})

export function ResetPassword() {
  const [params] = useSearchParams()
  const token = useMemo(() => params.get('token') || '', [params])
  const [done, setDone] = useState(false)
  const [error, setError] = useState('')

  const formik = useFormik({
    initialValues: {new_password: '', confirm: ''},
    validationSchema: schema,
    onSubmit: async (values) => {
      setError('')
      try {
        await axios.post(`${apiUrl}/admin/auth/reset-password`, {
          token,
          new_password: values.new_password,
        })
        setDone(true)
      } catch (err: any) {
        const msg = err?.response?.data?.message || err?.response?.data?.error || 'Reset failed.'
        setError(typeof msg === 'string' ? msg : 'Reset failed.')
      }
    },
  })

  if (!token) {
    return (
      <div className='text-center'>
        <p>Invalid or missing reset link.</p>
        <Link to='/auth/login'>Sign in</Link>
      </div>
    )
  }

  if (done) {
    return (
      <div className='text-center'>
        <p className='mb-5'>Your password was updated. You can sign in.</p>
        <Link to='/auth/login' className='btn btn-primary'>
          Sign in
        </Link>
      </div>
    )
  }

  return (
    <form className='form w-100' onSubmit={formik.handleSubmit} noValidate>
      <div className='text-center mb-10'>
        <h1 className='text-dark fw-bolder mb-3'>Set new password</h1>
      </div>
      {error ? <div className='alert alert-danger mb-5'>{error}</div> : null}

      <div className='fv-row mb-8'>
        <label className='form-label fw-bolder'>New password</label>
        <input
          type='password'
          autoComplete='new-password'
          {...formik.getFieldProps('new_password')}
          className={clsx(
            'form-control bg-transparent',
            formik.touched.new_password && formik.errors.new_password && 'is-invalid'
          )}
        />
        {formik.touched.new_password && formik.errors.new_password && (
          <div className='text-danger fs-7 mt-1'>{formik.errors.new_password}</div>
        )}
      </div>

      <div className='fv-row mb-8'>
        <label className='form-label fw-bolder'>Confirm password</label>
        <input
          type='password'
          autoComplete='new-password'
          {...formik.getFieldProps('confirm')}
          className={clsx(
            'form-control bg-transparent',
            formik.touched.confirm && formik.errors.confirm && 'is-invalid'
          )}
        />
        {formik.touched.confirm && formik.errors.confirm && (
          <div className='text-danger fs-7 mt-1'>{formik.errors.confirm}</div>
        )}
      </div>

      <div className='d-grid mb-5'>
        <button type='submit' className='btn btn-primary' disabled={formik.isSubmitting}>
          Update password
        </button>
      </div>
      <div className='text-center'>
        <Link to='/auth/login'>Back to sign in</Link>
      </div>
    </form>
  )
}
